import { FormattedMessage } from 'react-intl';
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';

import Tab from 'src/registry/components/navigation/Tab';
import TabRow from 'src/registry/components/navigation/TabRow';
import { MEASUREMENT_TYPE } from 'src/features/billableMetrics/utils/types';
import { useBillableMetricMap } from 'src/features/billableMetrics/hooks/useBillableMetricMap';
import { pricingOverridesMessages } from 'src/features/subscriptionItemPricingOverrides/utils/messages';
import { subItemQuantityEventMessages } from 'src/features/subscriptionItemQuantityEvents/utils/messages';
import { subItemUsageRecordMessages } from 'src/features/subscriptionItemUsageRecords/utils/messages';
import { SubscriptionItem } from 'src/features/subscriptionItems/utils/types';

type SubscriptionItemDashboardTablesProps = {
    subscriptionItem?: SubscriptionItem;
};

/**
 * Tab row for the subscription item dashboard. The panels are the child routes declared in
 * HomeRoutes.
 *
 * Which tabs exist depends on how the *plan* configures the item, not on permissions alone: a
 * fixed-price item is never measured, so quantity events and usage records would both be
 * permanently empty, and an item the plan does not open to scaling has no coefficients to show.
 * An item with none of the three renders no tab row at all rather than an empty one.
 */
const SubscriptionItemDashboardTables = ({ subscriptionItem }: SubscriptionItemDashboardTablesProps) => {
    const { pathname } = useLocation();
    const { subscriptionId = '', subscriptionItemId = '' } = useParams();
    const metricMap = useBillableMetricMap();

    const metric = subscriptionItem?.service_plan_item?.billable_metric
        ? metricMap.get(subscriptionItem.service_plan_item.billable_metric)
        : undefined;

    const tabs = [
        ...(subscriptionItem?.service_plan_item?.allows_price_coefficient
            ? [{ path: 'pricing-overrides', label: pricingOverridesMessages.pricingOverrides }]
            : []),
        ...(metric?.measurement_type === MEASUREMENT_TYPE.STATEFUL
            ? [{ path: 'quantity-events', label: subItemQuantityEventMessages.quantityEvents }]
            : []),
        ...(metric?.measurement_type === MEASUREMENT_TYPE.METERED
            ? [{ path: 'usage-records', label: subItemUsageRecordMessages.usageRecords }]
            : []),
    ];

    // The applicable set is only known once the item has loaded, so the index route cannot pick a
    // default statically — land on the first applicable tab here instead, and bounce a tab that
    // no longer applies (a metric swapped, scaling switched off) rather than showing a dead panel.
    if (subscriptionItem && tabs.length > 0) {
        const isOnAnApplicableTab = tabs.some(({ path }) => pathname.endsWith(`/${path}`));
        if (!isOnAnApplicableTab) {
            // Absolute: a relative redirect from an unknown tab resolves against that unknown
            // segment and appends the target on every pass, which never terminates.
            const base = `/subscriptions/${subscriptionId}/subscription-item/${subscriptionItemId}`;
            return <Navigate to={`${base}/${tabs[0].path}`} replace />;
        }
    }

    if (tabs.length === 0) return null;

    return (
        <div className="grid flex-3 grid-cols-1">
            <TabRow>
                {tabs.map(({ path, label }) => (
                    <Tab key={path} to={path} preventScrollReset>
                        <FormattedMessage {...label} />
                    </Tab>
                ))}
            </TabRow>
            <div className="mt-4">
                <Outlet />
            </div>
        </div>
    );
};

export default SubscriptionItemDashboardTables;
